import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Activity,
  AlertTriangle,
  Camera,
  CheckCircle2,
  CircleDot,
  Loader2,
  Power,
  RefreshCw,
  Search,
  SendHorizonal,
  ShieldCheck,
  ShieldQuestion,
  Square,
  Terminal,
  Wrench,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  ScrollArea,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { isEnterKey } from '@/lib/keyboard';
import { useOptibotStore } from '../stores/optibot-store';
import {
  useDeviceSessionStream,
  ConnState,
  EventType,
  TimelineKind,
  ToolStatus,
  type ApprovalRequestPayload,
  type DeviceSessionOutput,
  type SessionEvent,
  type SessionEventType,
  type TimelineItem,
} from '../hooks/useDeviceSessionStream';

interface Props {
  session: DeviceSessionOutput;
  /** 'inline' = embedded in an OptiBot chat message; 'fullpage' = the roomy focused/staff surface. */
  variant?: 'inline' | 'fullpage';
  /** 'live' opens the SSE; 'history' renders a static recorded transcript (no runner). */
  mode?: 'history' | 'live';
  initialEvents?: SessionEvent[];
  /** Screen name shown in the header so it's unambiguous which device this session/composer targets. */
  deviceLabel?: string;
  /** Fired ONCE when the session reaches a terminal state (conn → 'ended'). The host uses it to refetch the
   *  session list so its status badge doesn't stay stale-"Live" after the detail has ended (list ↔ detail sync). */
  onEnded?: () => void;
}

const ICON: Record<SessionEventType, typeof Activity> = {
  'session.started': CircleDot,
  diagnostic: Activity,
  proposal: ShieldQuestion,
  approval_request: ShieldQuestion,
  approval_grant: CheckCircle2,
  action: Wrench,
  verify: CheckCircle2,
  'user.message': CircleDot,
  'user.interrupt': Square,
  status: Activity, // never rendered in the timeline (handled as the busy badge), but the map is exhaustive
  error: AlertTriangle,
  'session.ended': CircleDot,
};

/** Raw MCP/device tool id → a short, human key. `mcp__device__device__read` → `read`, `device__exec` →
 *  `exec` (the raw ids are noise to users; mirrors the CLI's friendlyTool). */
function toolKey(raw?: string): string {
  const stripped = (raw ?? '')
    .replace(/^mcp__device__/, '')
    .replace(/^mcp__[^_]+__/, '')
    .replace(/^device__/, '');
  return stripped || raw || 'tool';
}
/** Knowledge/bookkeeping tool steps we don't surface — the agent's own diagnostic summary is what the
 *  customer needs, not the KB plumbing. `server_record` = silent this-device cloud check;
 *  `searchInternalDocs`/`readInternalDoc` = PRIVATE device-internal KB (other customers' tickets — the
 *  runner also scrubs their content); `searchArticles`/`readArticle` = PUBLIC support.optisigns.com
 *  lookups (not private, just noise — the agent paraphrases/cites them in its answer). Device tools
 *  (read/exec/screenshot) are NOT hidden — the customer should see the agent inspect/change the device. */
const HIDDEN_TOOL_KEYS = new Set([
  'server_record',
  'searchInternalDocs',
  'readInternalDoc',
  'searchArticles',
  'readArticle',
]);
/** Tool-specific icon: read = inspect, exec = run a command, screenshot = capture. */
const TOOL_ICON: Record<string, typeof Activity> = {
  read: Search,
  exec: Terminal,
  screenshot: Camera,
};
/** The command a device tool proposed (device__read/exec carry `params.command`). */
function proposalCommand(event: SessionEvent): string | undefined {
  const c = (event.payload as { params?: { command?: unknown } } | null)?.params?.command;
  return typeof c === 'string' ? c : undefined;
}

/** Short local time (e.g. "10:17:20 PM") + full date-time for the hover title; null on an unparseable ts. */
function fmtTime(ts: string): { short: string; full: string } | null {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return {
    short: d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }),
    full: d.toLocaleString(),
  };
}

/** Wrap a timeline row so hovering it reveals that step's exact time — a real (Radix) tooltip, so it's
 *  consistent on every row (the native `title` only fired on some and lagged). The single child is the row's
 *  `<li>`; with no parseable ts it passes through untouched. */
function TimeTip({ ts, children }: { ts: string; children: React.ReactElement }) {
  const f = fmtTime(ts);
  if (!f) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="left" className="text-[11px] tabular-nums">
        {f.full}
      </TooltipContent>
    </Tooltip>
  );
}

/** The shared device-troubleshoot session view: a streaming, status-stamped event timeline with
 *  inline HITL approval cards (the EXACT command shown before the gate) + Stop. Used inline in OptiBot
 *  and (later) at the staff route. Live writes go to device-agent-api via the session capability token. */
export function DeviceTroubleshootSessionView({
  session,
  variant = 'inline',
  mode = 'live',
  initialEvents,
  deviceLabel,
  onEnded,
}: Props) {
  const { t } = useTranslation();
  const { events, timeline, conn, busy, decide, interrupt, endSession, sendMessage, reconnect } =
    useDeviceSessionStream(session, { mode, initialEvents });

  // Report conn to the store so the in-chat session card can show a live status badge.
  const setSessionConn = useOptibotStore((s) => s.setSessionConn);
  useEffect(() => {
    if (mode === 'live') setSessionConn(session.sessionId, conn);
  }, [conn, mode, session.sessionId, setSessionConn]);

  // Notify the host ONCE when this session terminates (SSE `session.ended` OR our optimistic End), so it can
  // refetch the session list — otherwise the list badge stays stale-"Live" while the detail reads "Ended",
  // and a later Start/Continue targets a session that's actually gone. Ref-guarded so it fires exactly once
  // regardless of the callback's identity.
  const endedNotified = useRef(false);
  useEffect(() => {
    if (conn === ConnState.Ended && !endedNotified.current) {
      endedNotified.current = true;
      onEnded?.();
    }
  }, [conn, onEnded]);

  const viewportRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const onListScroll = () => {
    const el = viewportRef.current;
    if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };

  // An approval is still pending only if no approval_grant for its toolUseId has arrived.
  const granted = useMemo(
    () =>
      new Set(
        events
          .filter((e) => e.type === EventType.ApprovalGrant)
          .map((e) => (e.payload as { toolUseId?: string } | null)?.toolUseId)
          .filter((id): id is string => Boolean(id)),
      ),
    [events],
  );

  // The agent is blocked on a human decision (not "working") whenever an approval_request is still pending:
  // not pre-authorized (standing grant = auto, never blocks) AND no grant has arrived yet.
  const pendingApproval = useMemo(
    () =>
      events.some(
        (e) =>
          e.type === EventType.ApprovalRequest &&
          !(e.payload as ApprovalRequestPayload).preAuthorized &&
          !granted.has((e.payload as ApprovalRequestPayload).toolUseId),
      ),
    [events, granted],
  );

  // proposal events carry the tool name; action/error carry only a toolCallId. Correlate so a completed/
  // failed row can show WHICH tool ran (e.g. "✓ Read"), never the raw mcp__ id.
  const toolById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of events) {
      if (e.type !== EventType.Proposal) continue;
      const p = e.payload as { toolCallId?: string; title?: string } | null;
      if (p?.toolCallId && p.title) m.set(p.toolCallId, p.title);
    }
    return m;
  }, [events]);

  // CLI-parity progress cues. `booting` = still waiting for the agent's FIRST output: both the SSE handshake
  // (Connecting) AND the window after it goes Live but before any event/heartbeat arrives — the runner is
  // still booting (Fly microVM cold start + WG + reach-gate, ~15-60s). Previously that Live-but-silent window
  // showed NO spinner, so the session looked hung. `working` = the agent is mid-turn (heartbeat busy,
  // optimistically kicked on activity) AND not waiting on a human approval. The trailing WorkingIndicator
  // renders one or the other (spinner + elapsed), so the wait never looks frozen — same as the CLI.
  // A RESUMED session opens live with the prior transcript already seeded (initialEvents), so its tail is the
  // PRIOR `session.ended` and `timeline.length === 0` never holds. Treat "live + tail is a session-ended + no
  // activity yet" as booting too, so the reconnecting/resuming runner shows the spinner, not a dead end.
  const lastItem = timeline[timeline.length - 1];
  const tailIsEnded =
    lastItem?.kind === TimelineKind.Event && lastItem.event.type === EventType.SessionEnded;
  const booting =
    mode === 'live' &&
    (conn === ConnState.Connecting ||
      (conn === ConnState.Live &&
        !busy &&
        !pendingApproval &&
        (timeline.length === 0 || tailIsEnded)));
  const working = conn === ConnState.Live && busy && !pendingApproval;

  // The WorkingIndicator's elapsed "Ns" should measure the CURRENT step (time since the agent's last output),
  // not a running total over the whole session (which climbed to 100s of seconds). Use the highest durable
  // seq as a reset key — it bumps on every agent event (proposal/action/diagnostic) but NOT on seq=0
  // streaming deltas, so the counter restarts per step and reads small while the agent is actively producing.
  const lastActivitySeq = useMemo(
    () => events.reduce((m, e) => (e.seq > m ? e.seq : m), 0),
    [events],
  );

  // One timestamp at the top of the transcript (when it began) — per-row times just crowd the column;
  // the exact time of any individual step is still a hover away (each row carries its full date-time title).
  const startedAt = events[0] ? fmtTime(events[0].ts) : null;

  // Auto-scroll the timeline to the newest event — but only when the user is already near the bottom,
  // so scrolling up to read earlier output isn't yanked back down on each new event. `booting`/`working`
  // toggles add/remove the trailing spinner row, so scroll it into view too.
  useEffect(() => {
    const el = viewportRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [timeline, booting, working]);

  return (
    <Card
      data-testid="device-troubleshoot-view"
      className={cn('my-2', variant === 'fullpage' && 'flex h-full flex-col')}
    >
      <CardContent className={cn('p-3', variant === 'fullpage' && 'flex min-h-0 flex-1 flex-col')}>
        <div className="mb-3">
          {/* Row 1: title on its own line (it can wrap without squeezing the status/actions). */}
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <Wrench className="h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0">{deviceLabel ?? t('optibot.session.title')}</span>
          </div>
          {/* Who launched it — attribution/audit, mirrors the history list's "by {actor}". */}
          {session.actor && (
            <div className="mb-2 truncate text-[11px] text-muted-foreground/80">
              {t('optibot.session.startedBy', { actor: session.actor })}
            </div>
          )}
          {/* Row 2: status badges (left) + actions (right). */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ConnBadge conn={conn} />
              {/* Idle is the only state worth a header badge — "working"/"booting" are carried by the
                  trailing spinner; while waiting on an approval the inline card is the signal. */}
              {conn === ConnState.Live && !busy && !pendingApproval && <BusyBadge busy={false} />}
            </div>
            {conn === ConnState.Live && (
              <div className="flex items-center gap-1">
                {/* Interrupt = cancel the agent's current turn; the session stays live so you can keep steering. */}
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('optibot.session.stop')}
                  onClick={() => void interrupt()}
                >
                  <Square className="mr-1 h-3.5 w-3.5" />
                  {t('optibot.session.stop')}
                </Button>
                {/* End = tear down the runner + close the session — confirm first (it's terminal). */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      aria-label={t('optibot.session.end')}
                    >
                      <Power className="mr-1 h-3.5 w-3.5" />
                      {t('optibot.session.end')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('optibot.session.endConfirmTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('optibot.session.endConfirmBody')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('optibot.session.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => {
                          endSession().catch(() => toast.error(t('optibot.session.endFailed')));
                        }}
                      >
                        {t('optibot.session.end')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
            {/* Dropped connection is NOT terminal — the session keeps running on the runner. Offer a manual
                resume that re-opens the SSE from the replay cursor (auto-heal covers brief blips; this is the
                escape hatch when the 60s deadline lapsed). */}
            {(conn === ConnState.Error || conn === ConnState.Reconnecting) && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('optibot.session.reconnect')}
                onClick={() => void reconnect()}
              >
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
                {t('optibot.session.reconnect')}
              </Button>
            )}
          </div>
        </div>

        {(() => {
          const list = (
            <TooltipProvider delayDuration={200}>
              <ol className="space-y-2">
                {/* When the session began — shown once, at the top. */}
                {startedAt && (
                  <li className="pb-1 text-center text-[11px] text-muted-foreground/70">
                    {startedAt.full}
                  </li>
                )}
                {timeline.map((item) => {
                  // Agent's streamed answer (coalesced) — left, with the activity icon. Hover → its time.
                  if (item.kind === TimelineKind.Message) {
                    return (
                      <TimeTip key={item.id} ts={item.ts}>
                        <li>
                          <DiagnosticMessage
                            text={item.text}
                            streaming={!item.final && conn === ConnState.Live}
                          />
                        </li>
                      </TimeTip>
                    );
                  }
                  // A device tool step (read/exec/screenshot): command + status (✓/✗) + collapsible output,
                  // folded into ONE row — the result updates this row in place instead of appending below.
                  if (item.kind === TimelineKind.Tool) {
                    return (
                      <TimeTip key={item.id} ts={item.ts}>
                        <li>
                          <ToolStepRow item={item} live={conn === ConnState.Live} />
                        </li>
                      </TimeTip>
                    );
                  }
                  const ev = item.event;
                  // User's steering message — right-aligned chat bubble (distinct from agent output).
                  if (ev.type === EventType.UserMessage) {
                    return (
                      <TimeTip key={item.id} ts={ev.ts}>
                        <li>
                          <UserBubble text={summarize(ev)} />
                        </li>
                      </TimeTip>
                    );
                  }
                  // User interrupted the turn — a quiet centered marker.
                  if (ev.type === EventType.UserInterrupt) {
                    return (
                      <li key={item.id}>
                        <InterruptMarker />
                      </li>
                    );
                  }
                  if (
                    ev.type === EventType.ApprovalRequest &&
                    !(ev.payload as ApprovalRequestPayload).preAuthorized &&
                    !granted.has((ev.payload as ApprovalRequestPayload).toolUseId)
                  ) {
                    return (
                      <li key={item.id}>
                        <InlineApproval
                          req={ev.payload as ApprovalRequestPayload}
                          deviceName={session.deviceName}
                          onDecide={(d) => void decide(ev.payload as ApprovalRequestPayload, d)}
                        />
                      </li>
                    );
                  }
                  // Reaching here on an approval_request means it's pre-authorized / already-granted — a
                  // standing-grant (D12) auto-approval. Show it CLEARLY as "Auto-approved · <command>" (it's
                  // done + in the audit trail) instead of the raw `approval_request` event row.
                  if (ev.type === EventType.ApprovalRequest) {
                    return (
                      <TimeTip key={item.id} ts={ev.ts}>
                        <li>
                          <AutoApprovedRow event={ev} />
                        </li>
                      </TimeTip>
                    );
                  }
                  // Internal bookkeeping rows add no user value — the header shows session state and the
                  // approval card shows the grant. Skip them (mirrors the CLI).
                  if (ev.type === EventType.SessionStarted || ev.type === EventType.ApprovalGrant)
                    return null;
                  return (
                    <TimeTip key={item.id} ts={ev.ts}>
                      <li>
                        <EventRow event={ev} toolById={toolById} />
                      </li>
                    </TimeTip>
                  );
                })}
                {/* CLI-parity trailing spinner: sits under the latest event while the agent is booting or
                    working, with elapsed seconds so a long step never looks hung. */}
                {(booting || working) && (
                  <li>
                    <WorkingIndicator
                      phase={booting ? 'booting' : 'working'}
                      resetKey={lastActivitySeq}
                    />
                  </li>
                )}
                {timeline.length === 0 && !booting && !working && (
                  <li className="text-xs text-muted-foreground">{t('optibot.session.waiting')}</li>
                )}
              </ol>
            </TooltipProvider>
          );
          // Fullpage: scroll inside a styled ScrollArea (same thumb as the OptiBot chat). Inline: no scroll.
          return variant === 'fullpage' ? (
            <ScrollArea
              className="min-h-0 min-w-0 flex-1"
              // Radix wraps the viewport content in a `display:table` div that sizes to its content, so long
              // command/code lines overflow horizontally and get clipped by the bounded drawer. Force it to
              // `block` (w-full) so content WRAPS to the width instead. Same fix as FeedsBrowser.tsx.
              viewportClassName="[&>div]:!block"
              viewportRef={viewportRef}
              onScrollCapture={onListScroll}
              thumbClassName="bg-foreground/30 hover:bg-foreground/50"
            >
              {list}
            </ScrollArea>
          ) : (
            list
          );
        })()}

        {/* Steer THIS device's agent. The input lives inside the session card (labelled with the device),
            so it's spatially unambiguous which agent a prompt goes to — distinct from OptiBot's composer. */}
        {mode === 'live' && conn === ConnState.Live && (
          <SessionComposer onSend={(text) => void sendMessage(text)} />
        )}
      </CardContent>
    </Card>
  );
}

/** The session's own composer — prompts go to the device agent (sendMessage), never to OptiBot. */
function SessionComposer({ onSend }: { onSend: (text: string) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const send = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');
  }, [text, onSend]);
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (isEnterKey(e) && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    },
    [send],
  );
  return (
    <div className="mt-3 flex items-end gap-2 border-t border-border pt-2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('optibot.session.composerPlaceholder')}
        rows={1}
        className="min-h-[36px] max-h-[100px] resize-none text-xs"
      />
      <Button
        size="icon-md"
        variant="ghost"
        className="shrink-0 self-end text-primary hover:text-primary/80"
        onClick={send}
        disabled={!text.trim()}
        aria-label={t('optibot.send')}
      >
        <SendHorizonal className="h-4 w-4 fill-current" />
      </Button>
    </div>
  );
}

function ConnBadge({ conn }: { conn: ConnState }) {
  const { t } = useTranslation();
  const variant =
    conn === ConnState.Live ? 'default' : conn === ConnState.Error ? 'destructive' : 'secondary';
  return (
    <Badge variant={variant} className="text-[10px]">
      {t(`optibot.session.conn.${conn}`)}
    </Badge>
  );
}

/** While live: "Working…" (a turn is in progress) vs "Idle" (waiting for you) — from the runner heartbeat. */
function BusyBadge({ busy }: { busy: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-[10px]',
        busy ? 'text-primary' : 'text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          busy ? 'animate-pulse bg-primary' : 'bg-muted-foreground/40',
        )}
      />
      {busy ? t('optibot.session.working') : t('optibot.session.idle')}
    </span>
  );
}

/** CLI-parity progress spinner that trails the timeline while the agent is busy. `booting` covers the
 *  runner boot (no events yet); `working` covers a turn in progress. Shows elapsed seconds (counts from
 *  mount, so it resets each working stretch — same as the CLI spinner) so a long step never looks hung. */
function WorkingIndicator({ phase, resetKey }: { phase: 'booting' | 'working'; resetKey: number }) {
  const { t } = useTranslation();
  const [secs, setSecs] = useState(0);
  // Restart the elapsed timer whenever `resetKey` changes (a new agent event) → "Ns" = time on the CURRENT
  // step, not the whole session. During booting there are no events, so it counts the boot wait.
  useEffect(() => {
    setSecs(0);
    const started = Date.now();
    const id = setInterval(() => setSecs(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [resetKey]);
  const label = phase === 'booting' ? t('optibot.session.booting') : t('optibot.session.working');
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {/* 3-dot bounce — same cue as OptiBot's TypingIndicator (shared `optibot-dot-bounce` keyframe, globals.css). */}
      <span className="flex shrink-0 items-center gap-1" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60"
            style={{
              animation: 'optibot-dot-bounce 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </span>
      <span>
        {label}
        {secs >= 1 && <span className="ml-1 tabular-nums opacity-70">{secs}s</span>}
      </span>
    </div>
  );
}

/** The agent's streamed answer — rendered as markdown (tables, headings, code), never truncated. While
 *  the message is still streaming (no `final` yet) a blinking caret trails the text (the typewriter cue). */
function DiagnosticMessage({ text, streaming }: { text: string; streaming: boolean }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
      <div className="min-w-0 break-words text-foreground">
        <Markdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {text}
        </Markdown>
        {streaming && (
          <span
            className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-primary align-middle"
            aria-hidden
          />
        )}
      </div>
    </div>
  );
}

/** The human's steering message — a right-aligned primary bubble, mirroring the OptiBot chat's UserMessage,
 *  so user turns read as distinct from the agent's (left, icon'd) output. */
function UserBubble({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] break-words rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground">
        {text}
      </div>
    </div>
  );
}

/** Quiet centered marker for a user interrupt (turn cancelled, session still live). */
function InterruptMarker() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-1.5 py-1 text-[11px] text-muted-foreground">
      <Square className="h-3 w-3" />
      {t('optibot.session.interrupted')}
    </div>
  );
}

/** Compact markdown overrides for the diagnostic answer (tables/code/lists are common in agent output). */
const MD_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="mb-1">{children}</li>,
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="rounded bg-muted px-1 py-0.5">{children}</code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-muted p-2 last:mb-0">{children}</pre>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="min-w-full">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border-b border-border px-2 py-1 text-left font-medium">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border-b border-border px-2 py-1">{children}</td>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
};

function EventRow({ event, toolById }: { event: SessionEvent; toolById: Map<string, string> }) {
  const { t } = useTranslation();
  const p = (event.payload ?? {}) as { toolCallId?: string; title?: string; output?: string };
  // No visible per-row timestamp — it just repeats and crowds the column. The exact time is one hover away
  // (the enclosing TimeTip wraps this row in a tooltip).
  // action/error carry a capped, already-redacted output preview — collapsible so the timeline stays scannable.
  const output = p.output?.trim();
  const outputBlock = output && (
    <details className="ml-2 mt-0.5">
      <summary className="cursor-pointer text-muted-foreground/70 hover:text-muted-foreground">
        {t('optibot.session.output')}
      </summary>
      <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-muted-foreground">
        {output}
      </pre>
    </details>
  );

  const isProposal = event.type === EventType.Proposal;
  const isAction = event.type === EventType.Action;
  const isError = event.type === EventType.Error;

  // A tool step → a clean action line (icon + friendly verb + command), NEVER the raw mcp__ id. proposal
  // carries the tool name; action/error only a toolCallId, so correlate back to the proposal that started it.
  const rawTool = isProposal ? p.title : p.toolCallId ? toolById.get(p.toolCallId) : undefined;
  const key = rawTool ? toolKey(rawTool) : undefined;
  // Internal-only tool steps are never surfaced (server-record check + the agent's private device-internal
  // KB lookups). The agent narrates only its FINDING; the raw step stays out of the customer view. See
  // HIDDEN_TOOL_KEYS — public support-article lookups are NOT hidden.
  if (key && HIDDEN_TOOL_KEYS.has(key)) return null;
  if (key && (isProposal || isAction || isError)) {
    const label = t(`optibot.session.tool.${key}`, key);
    const cmd = isProposal ? proposalCommand(event) : undefined;
    const Icon = isError
      ? AlertTriangle
      : isAction
        ? CheckCircle2
        : (TOOL_ICON[key] ?? ShieldQuestion);
    return (
      <div className="flex items-start gap-2 text-xs">
        <Icon
          className={cn(
            'mt-0.5 h-3.5 w-3.5 shrink-0',
            isError ? 'text-destructive' : isAction ? 'text-muted-foreground' : 'text-primary',
          )}
        />
        <div className="min-w-0">
          <span className="font-medium text-foreground">
            {label}
            {isError ? ` ${t('optibot.session.tool.failed')}` : ''}
          </span>
          {cmd && <span className="ml-2 break-all font-mono text-muted-foreground">{cmd}</span>}
          {outputBlock}
        </div>
      </div>
    );
  }

  // Generic (verify / session.ended / unknown). session.ended carries a human message — show it alone, no label.
  const Icon = ICON[event.type] ?? CircleDot;
  const ended = event.type === EventType.SessionEnded;
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0',
          isError ? 'text-destructive' : 'text-muted-foreground',
        )}
      />
      <div className="min-w-0">
        {!ended && (
          <span className="font-medium text-foreground">
            {t(`optibot.session.event.${event.type}`, event.type)}
          </span>
        )}
        {summarize(event) && (
          <span className={cn('break-words text-muted-foreground', !ended && 'ml-2')}>
            {summarize(event)}
          </span>
        )}
        {outputBlock}
      </div>
    </div>
  );
}

/** A pre-authorized / already-granted approval_request = a standing-grant (D12) auto-approval. Render it as
 *  a clear "Auto-approved · <command>" audit row (done, not pending) rather than the raw `approval_request`
 *  event row — so the operator sees the gated change WAS made and the standing grant authorized it. */
function AutoApprovedRow({ event }: { event: SessionEvent }) {
  const { t } = useTranslation();
  const command = (event.payload as ApprovalRequestPayload | null)?.command;
  return (
    <div className="flex items-start gap-2 text-xs">
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <span className="font-medium text-foreground">{t('optibot.session.autoApproved')}</span>
        {command && (
          <span className="ml-2 break-all font-mono text-muted-foreground">{command}</span>
        )}
      </div>
    </div>
  );
}

/** A folded device tool step (buildTimeline collapses a `proposal` + its `action`/`error` by toolCallId):
 *  ONE row carrying the command, a status-driven icon (running → tool glyph, ok → ✓, error → ✗), and the
 *  collapsible output. The result lands ON this row in place — no separate item appended below. */
function ToolStepRow({
  item,
  live,
}: {
  item: Extract<TimelineItem, { kind: typeof TimelineKind.Tool }>;
  live: boolean;
}) {
  const { t } = useTranslation();
  const key = item.tool ? toolKey(item.tool) : undefined;
  // Internal-only tool steps are never surfaced (same rule as EventRow — see HIDDEN_TOOL_KEYS).
  if (key && HIDDEN_TOOL_KEYS.has(key)) return null;
  const isOk = item.status === ToolStatus.Ok;
  const isError = item.status === ToolStatus.Error;
  // Running = proposal seen, no result yet. Show a SPINNER while the session is live (so you can see which
  // parallel steps are still in flight); once the session has ended, a still-running step is abandoned, not
  // working — fall back to the static tool glyph (no perpetual spin on a dead session).
  const spinning = item.status === ToolStatus.Running && live;
  const label = key
    ? t(`optibot.session.tool.${key}`, key)
    : t('optibot.session.tool.read', 'read');
  const Icon = isError
    ? AlertTriangle
    : isOk
      ? CheckCircle2
      : spinning
        ? Loader2
        : key
          ? (TOOL_ICON[key] ?? ShieldQuestion)
          : ShieldQuestion;
  const output = item.output?.trim();
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0',
          isError ? 'text-destructive' : isOk ? 'text-muted-foreground' : 'text-primary',
          spinning && 'animate-spin',
        )}
      />
      <div className="min-w-0">
        <span className="font-medium text-foreground">
          {label}
          {isError ? ` ${t('optibot.session.tool.failed')}` : ''}
        </span>
        {item.command && (
          <span className="ml-2 break-all font-mono text-muted-foreground">{item.command}</span>
        )}
        {output && (
          <details className="ml-2 mt-0.5">
            <summary className="cursor-pointer text-muted-foreground/70 hover:text-muted-foreground">
              {t('optibot.session.output')}
            </summary>
            <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-muted-foreground">
              {output}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

function InlineApproval({
  req,
  deviceName,
  onDecide,
}: {
  req: ApprovalRequestPayload;
  deviceName?: string;
  onDecide: (d: 'allow' | 'deny') => void;
}) {
  const { t } = useTranslation();
  const [responded, setResponded] = useState(false);
  const respond = (d: 'allow' | 'deny') => {
    setResponded(true);
    onDecide(d);
  };
  return (
    <Card className="border-primary/30">
      <CardContent className="p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <ShieldQuestion className="h-4 w-4 text-primary" />
          <span>{t('optibot.session.approveWrite')}</span>
        </div>
        {/* The EXACT agent-proposed command, shown as-is. Approve runs THIS command; Deny rejects it. To
            change course, deny and tell the agent what to do instead (composer) — it re-proposes a fresh,
            gated command. We never let the human hand-edit the command (that would bypass the agent + audit). */}
        <pre className="mb-2 overflow-x-auto rounded-md bg-muted p-2 text-xs text-foreground">
          {req.command}
        </pre>
        {/* `req.target` is the deviceUUID the change is bound to — show the screen NAME, not the raw UUID. */}
        <p className="mb-3 text-xs text-muted-foreground">
          {t('optibot.session.runsOn', { device: deviceName ?? req.target })}
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="default" disabled={responded} onClick={() => respond('allow')}>
            {t('optibot.approve')}
          </Button>
          <Button size="sm" variant="ghost" disabled={responded} onClick={() => respond('deny')}>
            {t('optibot.deny')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function summarize(event: SessionEvent): string {
  const p = event.payload as Record<string, unknown> | null;
  if (!p || typeof p !== 'object') return '';
  const pick = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : undefined);
  // NB: no `title` — that's the raw tool id (mcp__device__…), rendered as a friendly label by EventRow instead.
  return (
    pick('text') ??
    pick('command') ??
    pick('message') ??
    pick('note') ??
    pick('status') ??
    pick('decision') ??
    ''
  );
}
